import React, { useEffect, useRef, useState } from 'react';
import { businesses, reminders, auth as authApi } from '../api';
import PageHeader from '../components/PageHeader';
import { COUNTRIES, getCountryConfig } from '../utils/countries';
import { initCurrencySettings } from '../utils/format';
import { passwordStrength } from '../utils/passwordStrength';

const ChevronIcon = ({ open }) => (
  <svg
    className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
    fill="none" stroke="currentColor" viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="card mb-6">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between mb-4 pb-3 border-b border-gray-100 text-left"
      >
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <ChevronIcon open={open} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out md:max-h-none ${open ? 'max-h-[2000px]' : 'max-h-0'}`}>
        {children}
      </div>
    </div>
  );
};

export default function Settings() {
  const [business, setBusiness] = useState(null);
  const [reminderSettings, setReminderSettings] = useState(null);
  const [form, setForm] = useState({
    name: '', address: '', gst_number: '', phone: '', email: '',
    bank_details: '', invoice_prefix: 'INV', payment_terms_days: 30,
    country: 'IN', reminder_include_payment_details: false, reminder_payment_details_text: '',
  });
  const [reminderForm, setReminderForm] = useState({ days_before_due: 3, days_after_due: 1 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoError, setLogoError] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const strength = passwordStrength(passwordForm.newPassword);

  const countryConfig = getCountryConfig(form.country);

  useEffect(() => {
    businesses.get().then(r => {
      setBusiness(r.data);
      setLogoUrl(r.data.logo_url || null);
      setForm({
        name: r.data.name || '',
        address: r.data.address || '',
        gst_number: r.data.gst_number || '',
        phone: r.data.phone || '',
        email: r.data.email || '',
        bank_details: r.data.bank_details || '',
        invoice_prefix: r.data.invoice_prefix || 'INV',
        payment_terms_days: r.data.payment_terms_days || 30,
        country: r.data.country || 'IN',
        reminder_include_payment_details: !!r.data.reminder_include_payment_details,
        reminder_payment_details_text: r.data.reminder_payment_details_text || '',
      });
    });
    reminders.get().then(r => {
      if (r.data) {
        setReminderSettings(r.data);
        setReminderForm({
          days_before_due: r.data.days_before_due,
          days_after_due: r.data.days_after_due,
        });
      }
    });
  }, []);

  const save = async () => {
    if (!business) return;
    setSaving(true);
    try {
      await businesses.update(business.id, form);
      if (reminderSettings) {
        await reminders.update(reminderSettings.id, reminderForm);
      }
      const config = getCountryConfig(form.country);
      initCurrencySettings(config.locale, config.currency, config.symbol);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await authApi.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordSuccess('Password updated successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError('');

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setLogoError('Only PNG and JPG files are allowed');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setLogoUrl(previewUrl);
    setUploadingLogo(true);
    try {
      const res = await businesses.uploadLogo(business.id, file);
      setBusiness(res.data);
      setLogoUrl(res.data.logo_url);
    } catch (err) {
      setLogoError(err?.response?.data?.error || 'Upload failed');
      setLogoUrl(business.logo_url || null);
    } finally {
      setUploadingLogo(false);
    }
  };

  if (!business) return (
    <div className="flex items-center justify-center h-40">
      <div className="w-7 h-7 border-4 border-primary-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Settings"
        subtitle="Configure your business details"
        actions={
          <button className="btn-primary flex items-center gap-2" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Changes'}
          </button>
        }
      />

      <Section title="Business Profile">
        <div className="space-y-4">
          <div>
            <label className="label">Business Logo</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleLogoSelect}
              className="hidden"
              id="logo-upload"
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingLogo}
            >
              {uploadingLogo ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
            </button>
            <p className="text-xs text-gray-400 mt-1">PNG or JPG. Appears on all generated invoices.</p>
            {logoError && <p className="text-xs text-red-500 mt-1">{logoError}</p>}

            <div className="mt-3 w-32 h-32 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Business logo" className="w-full h-full object-contain" />
              ) : (
                <span className="text-gray-400 text-xs text-center px-2">No logo uploaded</span>
              )}
            </div>
          </div>
        </div>
      </Section>

      <Section title="Login Information">
        <div className="space-y-4">
          {passwordSuccess && (
            <div className="px-3 py-2 rounded-lg text-sm bg-green-50 text-green-700">{passwordSuccess}</div>
          )}
          {passwordError && (
            <div className="px-3 py-2 rounded-lg text-sm bg-red-50 text-red-700">{passwordError}</div>
          )}
          <div>
            <label className="label">Current Password</label>
            <input
              type="password" className="input"
              value={passwordForm.currentPassword}
              onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
            />
          </div>
          <div>
            <label className="label">New Password</label>
            <input
              type="password" className="input" minLength={8}
              value={passwordForm.newPassword}
              onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            />
            {passwordForm.newPassword && (
              <div className="mt-2">
                <div className="flex gap-1">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= strength.level ? strength.color : 'bg-gray-200'}`} />
                  ))}
                </div>
                <p className={`text-xs mt-1 ${strength.textColor}`}>{strength.label}</p>
              </div>
            )}
          </div>
          <div>
            <label className="label">Confirm New Password</label>
            <input
              type="password" className="input" minLength={8}
              value={passwordForm.confirmNewPassword}
              onChange={e => setPasswordForm({ ...passwordForm, confirmNewPassword: e.target.value })}
            />
          </div>
          <div>
            <button
              type="button"
              className="btn-primary"
              onClick={changePassword}
              disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmNewPassword}
            >
              {changingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>
      </Section>

      <Section title="Business Information">
        <div className="space-y-4">
          <div>
            <label className="label">Country</label>
            <select
              className="input"
              value={form.country}
              onChange={e => setForm({ ...form, country: e.target.value })}
            >
              {Object.entries(COUNTRIES).map(([code, cfg]) => (
                <option key={code} value={code}>{cfg.name} ({cfg.currency})</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Sets currency to {countryConfig.currency} ({countryConfig.symbol}) across the app
            </p>
          </div>
          <div>
            <label className="label">Business Name <span className="text-red-500">*</span></label>
            <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="My Business Pvt Ltd" />
          </div>
          <div>
            <label className="label">Address</label>
            <textarea className="input resize-none min-h-[80px]" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder={countryConfig.addressPlaceholder} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Phone</label>
              <input className="input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder={countryConfig.phonePlaceholder} />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="billing@business.com" />
            </div>
          </div>
          <div>
            <label className="label">{countryConfig.taxLabel}</label>
            <input className="input font-mono" value={form.gst_number} onChange={e => setForm({ ...form, gst_number: e.target.value.toUpperCase() })} placeholder={countryConfig.taxPlaceholder} maxLength={20} />
          </div>
        </div>
      </Section>

      <Section title="Payment Details">
        <div className="space-y-4">
          <div>
            <label className="label">Bank Details</label>
            <textarea
              className="input resize-none min-h-[100px]"
              value={form.bank_details}
              onChange={e => setForm({ ...form, bank_details: e.target.value })}
              placeholder={countryConfig.bankPlaceholder}
            />
          </div>
        </div>
      </Section>

      <Section title="Invoice Settings">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Invoice Prefix</label>
              <input className="input" value={form.invoice_prefix} onChange={e => setForm({ ...form, invoice_prefix: e.target.value })} placeholder="INV" maxLength={10} />
              <p className="text-xs text-gray-400 mt-1">e.g. INV → INV-0001</p>
            </div>
            <div>
              <label className="label">Payment Terms (days)</label>
              <input className="input" type="number" min="1" max="365" value={form.payment_terms_days} onChange={e => setForm({ ...form, payment_terms_days: parseInt(e.target.value) || 30 })} />
              <p className="text-xs text-gray-400 mt-1">Default due date offset from invoice date</p>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Reminder Settings">
        <p className="text-sm text-gray-500 mb-4">Configure when to send payment reminders</p>
        <div className="space-y-4">
          <div>
            <label className="label">Send reminder before due date (days)</label>
            <input className="input max-w-xs" type="number" min="0" max="30"
              value={reminderForm.days_before_due}
              onChange={e => setReminderForm({ ...reminderForm, days_before_due: parseInt(e.target.value) || 0 })} />
            <p className="text-xs text-gray-400 mt-1">0 to disable pre-due reminders</p>
          </div>
          <div>
            <label className="label">Send reminder after overdue (days)</label>
            <input className="input max-w-xs" type="number" min="0" max="30"
              value={reminderForm.days_after_due}
              onChange={e => setReminderForm({ ...reminderForm, days_after_due: parseInt(e.target.value) || 0 })} />
            <p className="text-xs text-gray-400 mt-1">0 to disable post-due reminders</p>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span>
                <span className="block text-sm font-medium text-gray-700">Include payment details in reminder messages</span>
                <span className="block text-xs text-gray-400 mt-0.5">Appends your payment details text below to every WhatsApp reminder</span>
              </span>
              <input
                type="checkbox"
                className="w-5 h-5 accent-primary-700 flex-shrink-0 ml-4"
                checked={form.reminder_include_payment_details}
                onChange={e => setForm({ ...form, reminder_include_payment_details: e.target.checked })}
              />
            </label>
            {form.reminder_include_payment_details && (
              <div className="mt-3">
                <label className="label">Payment details to include in reminders</label>
                <textarea
                  className="input resize-none min-h-[90px]"
                  value={form.reminder_payment_details_text}
                  onChange={e => setForm({ ...form, reminder_payment_details_text: e.target.value })}
                  placeholder="UPI ID, PayPal link, bank account details, etc."
                />
              </div>
            )}
          </div>

          <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
            <strong>WhatsApp Reminders:</strong> BillTrack uses WhatsApp Web links to send reminders. Open any invoice and click "Send Reminder" to send a pre-filled reminder message to the client.
          </div>
        </div>
      </Section>

      <div className="flex justify-end pb-8">
        <button className="btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save All Settings'}
        </button>
      </div>
    </div>
  );
}
